import { CellFormatOptions, ColumnTable, Table } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SectionTitle from '../../../../../../components/text/SectionTitle';
import TableEmptyContent from '../../../../../../components/text/TableEmptyContent';
import { MainStudy } from '../../types';
import { formatDate } from '../../../../../../utils/dates';

interface MainStudiesProps {
  mainStudiesData: MainStudy[];
}

export const MainStudies = ({ mainStudiesData }: MainStudiesProps) => {
  const { t } = useTranslation();

  const texts = useMemo(() => {
    const prefix = 'studentManagement.educationalInformation';
    return {
      title: t(`${prefix}.title`),
      noRecords: t(`${prefix}.emptyContent.title`),
      noRecordsSubtitle: t(`${prefix}.emptyContent.subtitle`),
      columns: {
        academicLevel: t(`${prefix}.columns.academicLevel`),
        program: t(`${prefix}.columns.program`),
        status: t(`${prefix}.columns.status`),
        description: t(`${prefix}.columns.description`),
        dateOfAcquisition: t(`${prefix}.columns.dateOfAcquisition`),
        action: t(`${prefix}.columns.action`),
      },
      status: {
        in_progress: t(`${prefix}.status.in_progress`),
        completed: t(`${prefix}.status.completed`),
      },
    };
  }, [t]);

  const columns = useMemo<ColumnTable<MainStudy>[]>(
    () => [
      {
        headerText: texts.columns.academicLevel,
        columnName: 'academicLevel',
        tdClassName: 'text-center',
        thClassName: 'text-center',
      },
      {
        headerText: texts.columns.program,
        columnName: 'program',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.dateOfAcquisition,
        columnName: 'displayGraduationDate',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<MainStudy>) => {
          const { displayGraduationDate } = options.row;
          if (!displayGraduationDate) {
            return texts.status.in_progress;
          }
          return formatDate(displayGraduationDate, 'DD/MM/YYYY');
        },
      },
      {
        headerText: texts.columns.status,
        columnName: 'displayStatus',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<MainStudy>) => {
          const { displayStatus } = options.row;
          const statusText = texts.status[displayStatus];
          const backgroundColor =
            displayStatus === 'in_progress' ? '#FFE6A1' : '#CEF0DA';

          return (
            <span
              style={{
                backgroundColor,
                padding: '4px 12px',
                borderRadius: '25px',
                display: 'inline-block',
                fontWeight: 500,
              }}
            >
              {statusText}
            </span>
          );
        },
      },
    ],
    [texts],
  );

  return (
    <>
      <div className="w-100 mt-4">
        <SectionTitle text={texts.title} />
      </div>
      <div className="w-100 mt-4">
        <Table
          columns={columns}
          data={mainStudiesData}
          striped
          noResultsText={
            <TableEmptyContent
              title={texts.noRecords}
              subtitle={texts.noRecordsSubtitle}
            />
          }
        />
      </div>
    </>
  );
};
